import { useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import type { TimeBase } from '@jupi/shared'
import { getClubTimezone, timezoneLabel } from '@jupi/ui'

// Which clock the flight log reads and writes times in: UTC, as aviation
// logs, or the club's own zone. One per-viewer preference, remembered in
// this browser, shared by the list and the entry form so a pilot who logs
// in UTC also reads the log in UTC.

const KEY = 'jupi.flightLog.timeBase'
const listeners = new Set<() => void>()

function read(): TimeBase {
  try {
    return localStorage.getItem(KEY) === 'utc' ? 'utc' : 'local'
  } catch {
    return 'local'
  }
}

let current: TimeBase = read()

export function setTimeBase(next: TimeBase) {
  current = next
  try {
    localStorage.setItem(KEY, next)
  } catch {
    // Private browsing: it lasts until the page is reloaded.
  }
  for (const listener of listeners) listener()
}

export function useTimeBase(): { timeBase: TimeBase; timeZone: string } {
  const timeBase = useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => current,
  )
  return { timeBase, timeZone: timeBase === 'utc' ? 'UTC' : getClubTimezone() }
}

export function TimeBaseToggle({ size = 'small' }: { size?: 'small' | 'medium' }) {
  const { t } = useTranslation()
  const { timeBase } = useTimeBase()
  return (
    <ToggleButtonGroup exclusive size={size} value={timeBase} onChange={(_, v: TimeBase | null) => v && setTimeBase(v)}>
      <ToggleButton value="utc" sx={{ px: 2 }}>
        UTC
      </ToggleButton>
      <ToggleButton value="local" sx={{ px: 2 }}>
        {t('flightLog.localTime', { zone: timezoneLabel(getClubTimezone()) })}
      </ToggleButton>
    </ToggleButtonGroup>
  )
}
